import React, { useState } from 'react';

// Gráficos simples del módulo (sin librerías): barras horizontales de una serie
// y columnas agrupadas de dos series. Paleta validada para daltonismo
// (script dataviz, ΔE ≥ 18 deutan). Toda cifra "< 5" se muestra como texto,
// nunca como barra, para no insinuar el valor oculto.

export const COLOR_A = '#5b6ee1';   // serie principal
export const COLOR_B = '#2a9d8f';   // segunda serie
const GRIS = '#eef0f5';
const TINTA = '#1a1a2e';
const TINTA_2 = '#666';

export const numero = (c) => (c?.menor_5 ? '< 5' : (c?.valor ?? 0).toLocaleString('es-CO'));

function Tabla({ columnas, filas }) {
  return (
    <table style={es.tabla}>
      <thead><tr>{columnas.map(c => <th key={c} style={es.th}>{c}</th>)}</tr></thead>
      <tbody>{filas.map((f, i) => <tr key={i}>{f.map((v, j) => <td key={j} style={{ ...es.td, textAlign: j ? 'right' : 'left' }}>{v}</td>)}</tr>)}</tbody>
    </table>
  );
}

function Marco({ titulo, subtitulo, tabla, children, vacio }) {
  const [verTabla, setVerTabla] = useState(false);
  return (
    <div style={es.marco}>
      <div style={es.cabecera}>
        <div>
          <h4 style={es.titulo}>{titulo}</h4>
          {subtitulo && <div style={es.subtitulo}>{subtitulo}</div>}
        </div>
        {!vacio && <button onClick={() => setVerTabla(!verTabla)} style={es.alternar}>{verTabla ? 'Ver gráfico' : 'Ver tabla'}</button>}
      </div>
      {vacio ? <div style={es.vacio}>Sin datos en este período.</div> : verTabla ? tabla : children}
    </div>
  );
}

// datos: [{ etiqueta, valor, menor_5 }]
export function BarrasH({ titulo, subtitulo, datos }) {
  const max = Math.max(1, ...datos.map(d => d.valor || 0));
  return (
    <Marco titulo={titulo} subtitulo={subtitulo} vacio={!datos.length}
      tabla={<Tabla columnas={['', 'Cantidad']} filas={datos.map(d => [d.etiqueta, numero(d)])} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {datos.map(d => (
          <div key={d.etiqueta} title={`${d.etiqueta}: ${numero(d)}`}>
            <div style={es.etiqueta}>{d.etiqueta}</div>
            <div style={es.fila}>
              {d.menor_5 ? (
                <span style={es.oculto}>{'< 5'}</span>
              ) : (
                <>
                  <div style={{ ...es.barra, width: `${Math.max(d.valor ? 2 : 0, (d.valor / max) * 100)}%` }} />
                  <span style={es.valor}>{numero(d)}</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </Marco>
  );
}

// datos: [{ mes: '2026-03', a: n, b: n }]; series: [{ clave: 'a', nombre }, { clave: 'b', nombre }]
export function Columnas({ titulo, subtitulo, datos, series }) {
  const max = Math.max(1, ...datos.flatMap(d => series.map(s => d[s.clave] || 0)));
  const colores = [COLOR_A, COLOR_B];
  const nombreMes = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString('es-CO', { month: 'short', year: '2-digit' });
  return (
    <Marco titulo={titulo} subtitulo={subtitulo} vacio={!datos.length}
      tabla={<Tabla columnas={['Mes', ...series.map(s => s.nombre)]} filas={datos.map(d => [nombreMes(d.mes), ...series.map(s => d[s.clave])])} />}>
      <div style={es.leyenda}>
        {series.map((s, i) => <span key={s.clave} style={es.leyendaItem}><span style={{ ...es.muestra, background: colores[i] }} />{s.nombre}</span>)}
      </div>
      <div style={es.columnas}>
        {datos.map(d => (
          <div key={d.mes} style={es.grupo}>
            <div style={es.area}>
              {series.map((s, i) => (
                <div key={s.clave} style={es.pila} title={`${nombreMes(d.mes)} · ${s.nombre}: ${d[s.clave]}`}>
                  <span style={es.valorCol}>{d[s.clave] || ''}</span>
                  <div style={{ ...es.columna, background: colores[i], height: `${((d[s.clave] || 0) / max) * 100}%` }} />
                </div>
              ))}
            </div>
            <div style={es.mes}>{nombreMes(d.mes)}</div>
          </div>
        ))}
      </div>
    </Marco>
  );
}

// Tarjeta de indicador: etiqueta, valor y nota
export function Indicador({ etiqueta, valor, nota }) {
  return (
    <div style={es.tile}>
      <div style={es.tileEtiqueta}>{etiqueta}</div>
      <div style={es.tileValor}>{valor}</div>
      {nota && <div style={es.tileNota}>{nota}</div>}
    </div>
  );
}

const es = {
  marco: { background: '#fff', borderRadius: '14px', padding: '16px 18px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', minWidth: 0 },
  cabecera: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '14px' },
  titulo: { fontSize: '14.5px', fontWeight: 700, color: TINTA, margin: 0 },
  subtitulo: { fontSize: '12px', color: '#999', marginTop: '2px' },
  alternar: { background: 'none', border: 'none', color: '#667eea', cursor: 'pointer', fontSize: '12.5px', fontWeight: 600, padding: 0, fontFamily: 'inherit', whiteSpace: 'nowrap' },
  vacio: { color: '#aaa', fontSize: '13px', padding: '16px 0', textAlign: 'center' },
  etiqueta: { fontSize: '12.5px', color: TINTA_2, marginBottom: '3px' },
  fila: { display: 'flex', alignItems: 'center', gap: '8px', height: '18px' },
  barra: { height: '14px', background: COLOR_A, borderRadius: '0 4px 4px 0' },
  valor: { fontSize: '12.5px', fontWeight: 700, color: TINTA, fontVariantNumeric: 'tabular-nums' },
  oculto: { fontSize: '12.5px', fontWeight: 600, color: '#999', background: GRIS, borderRadius: '4px', padding: '0 6px' },
  leyenda: { display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '10px' },
  leyendaItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: TINTA_2 },
  muestra: { width: '10px', height: '10px', borderRadius: '3px' },
  columnas: { display: 'flex', gap: '10px', alignItems: 'flex-end', overflowX: 'auto', paddingBottom: '4px', borderBottom: `1px solid ${GRIS}` },
  grupo: { flex: '1 0 44px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  area: { display: 'flex', gap: '2px', alignItems: 'flex-end', height: '150px', width: '100%', justifyContent: 'center' },
  pila: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', width: '18px' },
  columna: { width: '100%', maxWidth: '18px', borderRadius: '4px 4px 0 0', minHeight: 0 },
  valorCol: { fontSize: '11px', fontWeight: 700, color: TINTA, marginBottom: '2px' },
  mes: { fontSize: '11px', color: '#999', marginTop: '6px', whiteSpace: 'nowrap' },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${GRIS}`, color: '#999', fontWeight: 700, fontSize: '11.5px', textTransform: 'uppercase' },
  td: { padding: '6px 8px', borderBottom: `1px solid ${GRIS}`, color: TINTA, fontVariantNumeric: 'tabular-nums' },
  tile: { background: '#fff', borderRadius: '14px', padding: '14px 16px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' },
  tileEtiqueta: { fontSize: '12px', color: '#888', fontWeight: 600 },
  tileValor: { fontSize: '24px', fontWeight: 700, color: TINTA, marginTop: '4px' },
  tileNota: { fontSize: '11.5px', color: '#aaa', marginTop: '2px' },
};
